import { Box, Divider, Stack, useTheme } from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import { useSimpleEventEdit } from '@verantos-dev/app-state-cohort-builder';
import { ApiWorkflowDialog, DropdownListItem } from '@verantos-dev/shared-components';
import { FilterTypeLegacy, SimpleEventTypeLegacy } from '@verantos-dev/types-cohorts';

import { CohortBuilderEditModalActions } from '../../../../components/CohortBuilderEditModal/CohortBuilderEditModalActions/CohortBuilderEditModalActions';
import { CohortBuilderEditModalFilter } from '../../../../components/CohortBuilderEditModal/CohortBuilderEditModalFilter/CohortBuilderEditModalFilter';
import { CohortBuilderEditModalFilterSection } from '../../../../components/CohortBuilderEditModal/CohortBuilderEditModalFilterSection/CohortBuilderEditModalFilterSection';
import { CohortBuilderEditModalSummary } from '../../../../components/CohortBuilderEditModal/CohortBuilderEditModalSummary/CohortBuilderEditModalSummary';
import { CohortBuilderInputField } from '../../../../components/CohortBuilderInputField/CohortBuilderInputField';
import { useEmitResearchAssistantUpdate } from '../../../../hooks/common/useResearchAssistantUpdateForStudyPeriodCard';
import { getCohortEditModalTitle } from '../../../../utils/getCohortEditModalTitle';
import { getShouldShowFieldError } from '../../../../utils/getShouldShowFieldError';
import { useSimpleEventCohortBuilderEditModalActions } from '../../cohort-builder-sections/SimpleEventSection/hooks/useSimpleEventCohortBuilderEditModalActions';

export const SimpleEventEditCard = () => {
  const intl = useIntl();
  const theme = useTheme();
  const restrictSaveProcess = useFeatureFlagSplit('restrict_save_process_on_error', false);
  const isEnhancedComboboxEnabled = useFeatureFlagSplit('concept_group_enhanced_dropdown', false);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});

  const updateFieldError = (id: string, error?: string) => {
    setFieldErrors((prev) => {
      const updatedErrors = { ...prev };
      if (error) {
        updatedErrors[id] = error;
      } else {
        delete updatedErrors[id];
      }
      return updatedErrors;
    });
  };

  const {
    modal,
    isSaveAllowed,
    onCancelSimpleEvent,
    onSaveSimpleEvent,
    onAddConceptFilterClick,
    onAddNumericFilterClick,
    onAddRelatedEntityFilterClick,
    onAddUniquenessFilterClick,
    name,
    type,
    onNameChange,
    onNameBlur,
    nameInError,
    nameErrorMsg,
    nameValidCharacters,

    onTypeChange,

    typeInputFieldTitleText,

    isSimpleEventIsInUse,
    enableAddFilterButtons,

    conceptFilters,
    numericFilters,
    relatedEntityFilters,
    uniquenessFilters,
    onFilterInputValueChange,
    onDeleteConceptFilterClick,
    onDeleteNumericFilterClick,
    onDeleteRelatedEntityFilterClick,
    onDeleteUniquenessFilterClick,

    inputFieldConfigMap,

    /** Auto Scroll when adding filters **/
    headerRef,
    dialogContentRef,
    conceptFilterRef,
    numericFilterRef,
    relatedEntityFilterRef,
    uniquenessFilterRef,

    isEditable,
    viewMode,
  } = useSimpleEventEdit();
  const actions = useSimpleEventCohortBuilderEditModalActions({
    type,
    uniquenessFilters,
    onAddConceptFilterClick,
    onAddNumericFilterClick,
    onAddRelatedEntityFilterClick,
    onAddUniquenessFilterClick,
  });
  const emitResearchAssistantUpdate = useEmitResearchAssistantUpdate(viewMode);

  // check if there are any errors in the simple event card
  const hasErrors = restrictSaveProcess
    ? Object.values(fieldErrors).some((error) => error !== undefined)
    : false;

  return (
    <ApiWorkflowDialog
      id="cohort-builder-simple-event-edit-modal"
      title={getCohortEditModalTitle(intl, modal ?? null, !!isEditable)}
      size="large"
      // isInitialLoading={isLoading}
      // initialLoadingError={conceptGroupsError}
      // onRetryInitialLoadingFetch={refetchConceptGroups}
      isValidForSubmit={isSaveAllowed && !nameInError && !hasErrors}
      onClose={onCancelSimpleEvent}
      onSubmit={() => onSaveSimpleEvent(emitResearchAssistantUpdate)} // TODO: update to call put endpoint
      // onSubmit={onSubmit}
      // isSubmitting={isSaveStudyArmLoading}
      // submittingError={saveStudyArmError}
      dialogContentStyleProps={{ paddingBottom: 0 }}
      onlyShowCloseButton={viewMode}
      dialogMessages={{
        entryCancelButtonText: viewMode
          ? intl.formatMessage({
              id: 'projects.cohortBuilderPage.simpleEventEditCard.cancelButtonText',
            })
          : undefined,
      }}
      ref={dialogContentRef as any}
      NonScrollBottomComponent={
        !viewMode && enableAddFilterButtons ? (
          <Box sx={(theme) => ({ px: theme.spacing(6) })}>
            <CohortBuilderEditModalActions id="simple-event" actions={actions} />
          </Box>
        ) : null
      }
    >
      <Box data-testid={'header'} ref={headerRef}>
        <Stack spacing={2} mb={2}>
          <CohortBuilderEditModalSummary<SimpleEventTypeLegacy>
            id="simple-event-summary"
            nameProps={{
              displayLabel: 'Name',
              placeholder: 'Enter event name',
              value: name,
              error: nameInError,
              helperText: nameInError ? nameErrorMsg : undefined,
              onChange: onNameChange,
              onBlur: onNameBlur,
              validCharacters: nameValidCharacters,
              viewMode,
            }}
            typeProps={{
              id: 'simple-event-type',
              title: typeInputFieldTitleText || '',
              label: 'Type',
              placeholder: 'Select event type',
              value: type || '',
              onChange: (event) =>
                onTypeChange(event.target.name, event.target.value as SimpleEventTypeLegacy),
              options: [
                SimpleEventTypeLegacy.CONDITION_OCCURRENCE,
                SimpleEventTypeLegacy.DRUG_EXPOSURE,
                SimpleEventTypeLegacy.MEASUREMENT,
                SimpleEventTypeLegacy.OBSERVATION,
                SimpleEventTypeLegacy.PROCEDURE_OCCURRENCE,
                SimpleEventTypeLegacy.VISIT_OCCURRENCE,
              ],
              viewMode,
            }}
            viewMode={viewMode}
            isCohortBuilderItemInUse={isSimpleEventIsInUse}
          />
        </Stack>
        <Divider
          dir="horizontal"
          sx={{ width: '100%', height: '1px', my: theme.spacing(4), color: theme.palette.grey[50] }}
        />
      </Box>
      {/* render concept filters */}
      {conceptFilters.length > 0 && (
        <Box data-testid="conceptFilterSection" ref={conceptFilterRef}>
          <CohortBuilderEditModalFilterSection
            id="concept-filters"
            key="concept-filters"
            title="Concept filters"
          >
            {conceptFilters.map(({ id: conceptFilterId, inputFields }) => {
              return (
                <CohortBuilderEditModalFilter
                  key={conceptFilterId}
                  id={conceptFilterId}
                  onRemoveFilter={onDeleteConceptFilterClick}
                  viewMode={viewMode}
                >
                  {inputFields.map(({ id: inputFieldId, value, dirty }, filterIndex) => {
                    if (!inputFieldConfigMap[inputFieldId]) {
                      throw new Error(
                        `Input field config not found for input field id: ${inputFieldId}`
                      );
                    }
                    const { inputType, width, label, options, placeholder, helperText } =
                      inputFieldConfigMap[inputFieldId];

                    const onChange = (_value: string) => {
                      onFilterInputValueChange(
                        conceptFilterId,
                        inputFieldId,
                        _value,
                        FilterTypeLegacy.CONCEPT
                      );
                    };

                    return (
                      <CohortBuilderInputField
                        key={`simpleEventEditCard-conceptFilter-${inputFieldId}`}
                        id={inputFieldId}
                        value={value}
                        inputType={inputType}
                        width={width}
                        label={label}
                        options={options}
                        placeholder={placeholder}
                        helperText={helperText}
                        onChange={onChange}
                        viewMode={viewMode}
                        multiple={filterIndex === 2}
                        showFieldError={dirty && getShouldShowFieldError(modal)}
                        updateFieldError={updateFieldError}
                        fieldErrorText={intl.formatMessage({
                          id: 'projects.cohortBuilderPage.inputFieldError.conceptGroupFieldError',
                        })}
                        showCheckboxesInCombobox={!!isEnhancedComboboxEnabled && filterIndex === 2}
                        showIconsInCombobox={!!isEnhancedComboboxEnabled && filterIndex === 2}
                      />
                    );
                  })}
                </CohortBuilderEditModalFilter>
              );
            })}
          </CohortBuilderEditModalFilterSection>
          <Box sx={(theme) => ({ height: theme.spacing(4), width: theme.spacing(4) })} />
        </Box>
      )}
      {/* render numeric filters */}
      {numericFilters.length > 0 && (
        <Box data-testid="numericFilterSection" ref={numericFilterRef}>
          <CohortBuilderEditModalFilterSection
            id="numeric-filters"
            key="numeric-filters"
            title="Numeric filters"
          >
            {numericFilters.map(({ id: numericFilterId, inputFields }) => {
              return (
                <CohortBuilderEditModalFilter
                  key={numericFilterId}
                  id={numericFilterId}
                  viewMode={viewMode}
                  onRemoveFilter={onDeleteNumericFilterClick}
                >
                  {inputFields.map(({ id: inputFieldId, value }) => {
                    if (!inputFieldConfigMap[inputFieldId]) {
                      throw new Error(
                        `Input field config not found for input field id: ${inputFieldId}`
                      );
                    }
                    const { inputType, width, label, options, placeholder, helperText } =
                      inputFieldConfigMap[inputFieldId];
                    const onChange = (_value: string) => {
                      onFilterInputValueChange(
                        numericFilterId,
                        inputFieldId,
                        _value,
                        FilterTypeLegacy.NUMERIC
                      );
                    };
                    return (
                      <CohortBuilderInputField
                        key={`simpleEventEditCard-numericFilter-${inputFieldId}`}
                        id={inputFieldId}
                        value={value}
                        inputType={inputType}
                        width={width}
                        label={label}
                        options={options}
                        placeholder={placeholder}
                        helperText={helperText}
                        onChange={onChange}
                        viewMode={viewMode}
                      />
                    );
                  })}
                </CohortBuilderEditModalFilter>
              );
            })}
          </CohortBuilderEditModalFilterSection>
          <Box sx={(theme) => ({ height: theme.spacing(4), width: theme.spacing(4) })} />
        </Box>
      )}
      {/* render related entity filters */}
      {relatedEntityFilters.length > 0 && (
        <Box data-testid="relatedEntryFilterSection" ref={relatedEntityFilterRef}>
          <CohortBuilderEditModalFilterSection
            id="related-entity-filters"
            key="related-entity-filters"
            title="Related entity filters"
          >
            {relatedEntityFilters.map(({ id: relatedEntityFilterId, inputFields }) => {
              return (
                <CohortBuilderEditModalFilter
                  key={relatedEntityFilterId}
                  id={relatedEntityFilterId}
                  viewMode={viewMode}
                  onRemoveFilter={onDeleteRelatedEntityFilterClick}
                >
                  {inputFields.map(({ id: inputFieldId, value }, filterIndex) => {
                    if (!inputFieldConfigMap[inputFieldId]) {
                      throw new Error(
                        `Input field config not found for input field id: ${inputFieldId}`
                      );
                    }

                    const { inputType, width, label, options, placeholder, helperText } =
                      inputFieldConfigMap[inputFieldId];

                    const onChange = (_value: string) => {
                      !viewMode &&
                        onFilterInputValueChange(
                          relatedEntityFilterId,
                          inputFieldId,
                          _value,
                          FilterTypeLegacy.RELATED_ENTITY
                        );
                    };

                    return (
                      <CohortBuilderInputField
                        key={`simpleEventEditCard-relatedEntityFilter-${inputFieldId}`}
                        id={inputFieldId}
                        value={value}
                        inputType={inputType}
                        width={width}
                        label={label}
                        options={options}
                        placeholder={placeholder}
                        helperText={helperText}
                        onChange={onChange}
                        viewMode={viewMode}
                        multiple={filterIndex === 3}
                        showFieldError={getShouldShowFieldError(modal)}
                        updateFieldError={updateFieldError}
                        fieldErrorText={intl.formatMessage({
                          id: 'projects.cohortBuilderPage.inputFieldError.conceptGroupFieldError',
                        })}
                        showCheckboxesInCombobox={!!isEnhancedComboboxEnabled && filterIndex === 3}
                        showIconsInCombobox={!!isEnhancedComboboxEnabled && filterIndex === 3}
                      />
                    );
                  })}
                </CohortBuilderEditModalFilter>
              );
            })}
          </CohortBuilderEditModalFilterSection>
          <Box sx={(theme) => ({ height: theme.spacing(4), width: theme.spacing(4) })} />
        </Box>
      )}
      {/* render uniqueness filters */}
      {uniquenessFilters.length > 0 && (
        <Box data-testid="uniquencessFilterSection" ref={uniquenessFilterRef}>
          <CohortBuilderEditModalFilterSection
            id="uniqueness-filters"
            key="uniqueness-filters"
            title="Uniqueness filters"
          >
            {uniquenessFilters.map(({ id: uniquenessFilterId, inputFields }) => {
              return (
                <CohortBuilderEditModalFilter
                  key={uniquenessFilterId}
                  id={uniquenessFilterId}
                  viewMode={viewMode}
                  onRemoveFilter={onDeleteUniquenessFilterClick}
                >
                  {inputFields.map(({ id: inputFieldId, value }) => {
                    if (!inputFieldConfigMap[inputFieldId]) {
                      throw new Error(
                        `Input field config not found for input field id: ${inputFieldId}`
                      );
                    }

                    const {
                      inputType,
                      width,
                      label,
                      options: _options,
                      placeholder,
                      helperText,
                    } = inputFieldConfigMap[inputFieldId];

                    const options = (_options as DropdownListItem[])?.filter((option) => {
                      if (label === 'Filter type') {
                        return !(option.value === 'MINIMUM_BETWEEN');
                      }

                      return true;
                    });

                    const onChange = (_value: string) => {
                      onFilterInputValueChange(
                        uniquenessFilterId,
                        inputFieldId,
                        _value,
                        FilterTypeLegacy.UNIQUENESS
                      );
                    };
                    return (
                      <CohortBuilderInputField
                        key={`simpleEventEditCard-uniquenessFilter-${inputFieldId}`}
                        id={inputFieldId}
                        value={value}
                        inputType={inputType}
                        width={width}
                        label={label}
                        options={options}
                        placeholder={placeholder}
                        helperText={helperText}
                        onChange={onChange}
                        viewMode={viewMode}
                      />
                    );
                  })}
                </CohortBuilderEditModalFilter>
              );
            })}
          </CohortBuilderEditModalFilterSection>
          <Box sx={(theme) => ({ height: theme.spacing(4), width: theme.spacing(4) })} />
        </Box>
      )}
    </ApiWorkflowDialog>
  );
};
